import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Stat, StatGroup, StatusPill } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useResellerStats, useUpdateReseller, type ResellerRow } from '@/api/resellers';
import { InfoGrid } from '@/pages/account/components/InfoGrid';
import { EntityLink } from '@/components/entity-link';
import { formatDate, formatMoney } from '@/lib/format';
import { resellerStatusTone, resellerTierTone, humanize } from '../components/reseller-tones';

interface ResellerOverviewTabProps {
  reseller: ResellerRow;
  onRefetch: () => void;
}

const TIER_OPTIONS = [
  { label: 'Bronze', value: 'bronze' },
  { label: 'Silver', value: 'silver' },
  { label: 'Gold', value: 'gold' },
  { label: 'Platinum', value: 'platinum' },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Suspended', value: 'suspended' },
];

/** In-place edit form for reseller name/tier/status (mirrors the Promo overview edit). */
function ResellerEditForm({
  reseller,
  onSaved,
  onCancel,
}: {
  reseller: ResellerRow;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const toast = useToast();
  const update = useUpdateReseller(reseller.reseller_id);
  const [name, setName] = useState(reseller.reseller_name);
  const [tier, setTier] = useState(String(reseller.reseller_tier ?? ''));
  const [status, setStatus] = useState(String(reseller.reseller_status ?? ''));
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    try {
      await update.mutateAsync({
        reseller_name: name.trim() || undefined,
        reseller_tier: tier || undefined,
        reseller_status: status || undefined,
      });
      toast.success('Reseller updated');
      onSaved();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'Failed to update reseller.';
      setError(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit reseller</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {error ? <Alert tone="danger" title={error} /> : null}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--vn-space-4)',
            }}
          >
            <Field label="Reseller Name" required>
              {({ id }) => (
                <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />
              )}
            </Field>
            <Field label="Tier">
              {({ id }) => (
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger id={id}>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Status">
              {({ id }) => (
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id={id}>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
            <Button variant="ghost" type="button" onClick={onCancel}>Cancel</Button>
            <Button variant="primary" type="button" loading={update.isPending} onClick={save}>
              Save changes
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Overview tab: basic info grid + customer account card + performance stats. */
export function ResellerOverviewTab({ reseller, onRefetch }: ResellerOverviewTabProps) {
  const { data: statsData, isLoading: statsLoading } = useResellerStats(reseller.reseller_id);
  const stats = statsData?.result;
  const [editing, setEditing] = useState(false);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Basic information */}
      <Card>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardTitle>Basic Information</CardTitle>
            {!editing ? (
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <InfoGrid
            items={[
              { label: 'Reseller ID', value: reseller.reseller_id },
              { label: 'Reseller Name', value: reseller.reseller_name },
              {
                label: 'Tier',
                value: reseller.reseller_tier ? (
                  <StatusPill tone={resellerTierTone(reseller.reseller_tier)}>
                    {humanize(reseller.reseller_tier)}
                  </StatusPill>
                ) : '—',
              },
              {
                label: 'Status',
                value: (
                  <StatusPill tone={resellerStatusTone(reseller.reseller_status)}>
                    {humanize(reseller.reseller_status)}
                  </StatusPill>
                ),
              },
              { label: 'Organizations', value: reseller.organization_count },
              { label: 'Created', value: formatDate(reseller.created_at) },
              { label: 'Updated', value: formatDate(reseller.updated_at) },
            ]}
          />
        </CardBody>
      </Card>

      {/* Inline edit form */}
      {editing ? (
        <ResellerEditForm
          reseller={reseller}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onRefetch();
          }}
        />
      ) : null}

      {/* Linked customer account */}
      {reseller.customer && (
        <Card>
          <CardHeader>
            <CardTitle>Linked Customer Account</CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid
              items={[
                {
                  label: 'Customer',
                  value: (
                    <EntityLink
                      to={`/customers/${reseller.customer.customer_id}`}
                      id={reseller.customer.customer_id}
                      name={reseller.customer.customer_name}
                    />
                  ),
                },
                { label: 'Email', value: reseller.customer.customer_email },
              ]}
            />
          </CardBody>
        </Card>
      )}

      {/* Performance stats */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Summary</CardTitle>
        </CardHeader>
        <CardBody>
          {statsLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--vn-space-4)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  <Skeleton style={{ height: 16, width: 80 }} />
                  <Skeleton style={{ height: 28, width: 100 }} />
                </div>
              ))}
            </div>
          ) : (
            <StatGroup columns={4}>
              <Stat
                label="Total Services"
                value={String(stats?.total_services ?? 0)}
                hint={`${stats?.active_services ?? 0} active`}
              />
              <Stat
                label="Monthly Recurring"
                value={formatMoney(stats?.mrr ?? 0)}
              />
              <Stat
                label="Total Revenue"
                value={formatMoney(stats?.total_revenue ?? 0)}
              />
              <Stat
                label="Pending Revenue"
                value={formatMoney(stats?.pending_revenue ?? 0)}
              />
            </StatGroup>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
